import { EVINCED_SEVERITIES_IDS, TFilterOptions } from '@evinced-private/ui-common';

import { transformLabels } from 'src/apps/mfa/helpers/QueryHelpers';
import { IMfaSessionRow, TMfaSessionsResult } from 'src/apps/mfa/types/MfaDashboardServiceTypes';
import { returnNonNull } from 'src/common/helpers/ArraysHelper';
import { ILabelConfig } from 'src/common/types/LabelsTypes';

import { MfaSessionsQuery } from '../MfaSessions.generated';

export const transformMfaSessionsResults = (
	rawResults: MfaSessionsQuery,
	filterOptions?: TFilterOptions,
	labelsConfig?: ILabelConfig[]
): TMfaSessionsResult => {
	const rows: IMfaSessionRow[] =
		rawResults.mfa_session_runs?.filter(returnNonNull).map((session) => {
			const allLabels = transformLabels(
				session.custom_fields,
				session.app,
				session.app_version,
				labelsConfig
			);

			return {
				sessionId: session.run_id,
				sessionName: session.session_name,
				totalScans: session.total_scans,
				totalIssues: session.total_issues,
				criticalIssues: session.critical_issues,
				seriousIssues: session.serious_issues,
				moderateIssues: session.moderate_issues,
				minorIssues: session.minor_issues,
				bestPracticeIssues: session.best_practice_issues,
				needsReviewIssues: session.needs_review_issues,
				lastModified: session.run_time,
				owner: session.user_name,
				labels: allLabels,
				platform: session.os,
				appName: session.app,
				appVersion: session.app_version
			};
		}) ?? [];

	// Apply severity filtering for multiple selections only
	let filteredRows = [...rows];
	if (filterOptions?.severity && filterOptions.severity.length > 1) {
		const selectedSeverities = filterOptions.severity.map((s) => s.value);
		filteredRows = rows.filter((row) => {
			return selectedSeverities.some((severity) => {
				switch (severity) {
					case EVINCED_SEVERITIES_IDS.CRITICAL:
						return row.criticalIssues > 0;
					case EVINCED_SEVERITIES_IDS.SERIOUS:
						return row.seriousIssues > 0;
					case EVINCED_SEVERITIES_IDS.MODERATE:
						return row.moderateIssues > 0;
					case EVINCED_SEVERITIES_IDS.MINOR:
						return row.minorIssues > 0;
					case EVINCED_SEVERITIES_IDS.BEST_PRACTICE:
						return row.bestPracticeIssues > 0;
					case EVINCED_SEVERITIES_IDS.NEEDS_REVIEW:
						return row.needsReviewIssues > 0;
					default:
						return false;
				}
			});
		});
	}

	const total = rawResults.total?.[0]?.run_id_count ?? 0;
	const unfilteredTotal = rawResults.unfiltered_total?.[0]?.run_id_count ?? 0;

	const hasFrontendFiltering = filterOptions?.severity && filterOptions.severity.length > 1;
	const finalTotal = hasFrontendFiltering ? filteredRows.length : total;

	return {
		rows: filteredRows,
		total: finalTotal,
		unfilteredTotal
	};
};
